import { useEffect, useRef, useState } from "react";
import { Clock, BarChart3, MessageSquare, Sparkles } from "lucide-react";

interface AIBenefit {
  icon: React.ReactNode;
  title: string;
  description: string;
  example: string;
}

const aiBenefits: AIBenefit[] = [
  {
    icon: <Clock className="w-8 h-8" />,
    title: "Automatización de tareas repetitivas",
    description: "La IA hace el trabajo tedioso por ti",
    example: "Facturas, emails, informes... automáticos",
  },
  {
    icon: <BarChart3 className="w-8 h-8" />,
    title: "Análisis de datos sin analistas",
    description: "Entiende tus números sin contratar expertos",
    example: "Predicciones de ventas, patrones de clientes",
  },
  {
    icon: <MessageSquare className="w-8 h-8" />,
    title: "Atención al cliente 24/7",
    description: "Nunca pierdas una consulta",
    example: "Chatbots que resuelven, no frustran",
  },
  {
    icon: <Sparkles className="w-8 h-8" />,
    title: "Contenido profesional",
    description: "Textos, imágenes y vídeos de calidad",
    example: "Marketing que antes costaba miles",
  },
];

interface FutureTech {
  year: string;
  title: string;
  description: string;
}

const futureTechs: FutureTech[] = [
  {
    year: "2025",
    title: "Agentes IA Autónomos",
    description: "IAs que completan tareas complejas sin supervisión",
  },
  {
    year: "2026",
    title: "Robotaxis Urbanos",
    description: "Waymo, Tesla expandiéndose globalmente",
  },
  {
    year: "2027",
    title: "Neuralink Comercial",
    description: "Interfaces cerebro-ordenador para uso médico",
  },
  {
    year: "2028",
    title: "Computación Cuántica Útil",
    description: "Primeras aplicaciones prácticas fuera del laboratorio",
  },
  {
    year: "2030",
    title: "Conducción Autónoma Nivel 5",
    description: "Coches totalmente autónomos en cualquier condición",
  },
  {
    year: "2030+",
    title: "AGI",
    description: "Inteligencia Artificial General",
  },
];

const FutureSection = () => {
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            futureTechs.forEach((_, index) => {
              setTimeout(() => {
                setVisibleItems((prev) => new Set([...prev, index]));
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    const benefitsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setBenefitsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    if (benefitsRef.current) {
      benefitsObserver.observe(benefitsRef.current);
    }

    return () => {
      observer.disconnect();
      benefitsObserver.disconnect();
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header - Apple style */}
      <div className="text-center mb-16">
        <p className="text-sm font-light tracking-[0.2em] text-white/40 uppercase mb-6">
          Lo que viene para las PYMEs
        </p>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
          La IA va a transformar<br />
          <span className="text-white/60">cómo trabajas.</span>
        </h2>
        <p className="text-xl md:text-2xl font-light text-white/60 max-w-3xl mx-auto">
          Igual que te acompañamos del papel al digital, del servidor local al Cloud.<br />
          Ahora añadimos una nueva capa: <span className="text-white">la Inteligencia Artificial</span>.
        </p>
      </div>

      {/* AI Benefits for SMEs */}
      <div ref={benefitsRef} className="mb-24">
        <h3 className="text-2xl md:text-3xl font-light text-white text-center mb-12">
          Lo que la IA puede hacer por tu PYME
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {aiBenefits.map((benefit, index) => (
            <div
              key={benefit.title}
              className={`
                p-8 border border-white/10 rounded-2xl bg-white/5
                transition-all duration-700 ease-out
                ${benefitsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
              `}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-5 text-white">
                {benefit.icon}
              </div>
              <h4 className="text-xl font-light text-white mb-2">
                {benefit.title}
              </h4>
              <p className="text-base font-light text-white/60 mb-3">
                {benefit.description}
              </p>
              <p className="text-sm font-light text-white/40 italic">
                "{benefit.example}"
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Promise - No Smoke */}
      <div className="text-center mb-24 py-12 border-y border-white/10">
        <p className="text-2xl md:text-3xl font-light text-white mb-4">
          "No vendemos humo."
        </p>
        <p className="text-lg md:text-xl font-light text-white/60 max-w-2xl mx-auto">
          Probamos cada herramienta y recomendamos solo lo que funciona.<br />
          <span className="text-white">Mantenemos las capas de seguridad, Cloud y mantenimiento</span> mientras añadimos IA.
        </p>
      </div>

      {/* Future Technologies */}
      <div ref={containerRef}>
        <div className="text-center mb-12">
          <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
            El futuro que viene
          </h3>
          <p className="text-lg font-light text-white/50">
            Tecnologías que seguimos de cerca
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {futureTechs.map((tech, index) => {
            const isVisible = visibleItems.has(index);

            return (
              <div
                key={tech.year}
                className={`
                  bg-black p-10 md:p-12
                  transition-all duration-700 ease-out
                  ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
                `}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl md:text-5xl font-light text-white/30 mb-4">
                  {tech.year}
                </div>
                <h3 className="text-xl md:text-2xl font-light text-white mb-3">
                  {tech.title}
                </h3>
                <p className="text-base font-light text-white/50">
                  {tech.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FutureSection;
